"use client";

import { useCommunicationBodyQuery } from "@/lib/hooks/use-communication-body-query";

export type HydratedCommunicationBodyState = {
  /** Resolved full body (inline or S3), or null while loading / on empty. */
  body: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Client dual-read for communication bodies after UC-COMMS-BODY offload.
 * When `bodyS3Key` is set and inline `body` is empty, fetches
 * `GET /api/communications/[id]/body` (session cookies) through the shared
 * `["communication-body", id]` query, so reopening the same message is served
 * from cache instead of re-reading S3.
 */
export function useHydratedCommunicationBody(options: {
  communicationId: string | null | undefined;
  inlineBody?: string | null;
  bodyS3Key?: string | null;
  /** Fallback while loading / if hydrate fails (usually body_preview). */
  previewSnippet?: string | null;
}): HydratedCommunicationBodyState {
  const communicationId =
    typeof options.communicationId === "string" ? options.communicationId.trim() : "";
  const inline =
    typeof options.inlineBody === "string" ? options.inlineBody.trim() : "";
  const bodyS3Key =
    typeof options.bodyS3Key === "string" ? options.bodyS3Key.trim() : "";
  const preview =
    typeof options.previewSnippet === "string" ? options.previewSnippet.trim() : "";

  const needsS3Hydrate = Boolean(communicationId && bodyS3Key && !inline);

  const query = useCommunicationBodyQuery(communicationId, { enabled: needsS3Hydrate });

  const fetched = needsS3Hydrate && typeof query.data === "string" ? query.data.trim() : "";
  const fullBody = fetched || null;
  const loading = needsS3Hydrate && query.isPending;
  const error =
    needsS3Hydrate && query.isError ? "Could not load the full message from storage." : null;

  const body = inline || fullBody || preview || null;
  return { body, loading, error };
}
